/**
 * WeddingOrganizer360 - Main App Component
 */

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';

// Layouts
import MainLayout from '@components/layout/MainLayout.jsx';
import DashboardLayout from '@components/layout/DashboardLayout.jsx';
import GuestLayout from '@components/layout/GuestLayout.jsx';
import AuthLayout from '@components/layout/AuthLayout.jsx';

// Components
import Loader from '@components/common/Loader';
import PrivateRoute from '@components/common/PrivateRoute';
import ErrorBoundary from '@components/common/ErrorBoundary';

// Lazy load pages for code splitting
const Home = lazy(() => import('@pages/Home'));
const About = lazy(() => import('@pages/About'));
const Contact = lazy(() => import('@pages/Contact'));

// Auth pages
const Login = lazy(() => import('@pages/Auth/Login'));
const Register = lazy(() => import('@pages/Auth/Register'));
const ForgotPassword = lazy(() => import('@pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@pages/Auth/ResetPassword'));

// Vendor pages
const VendorListing = lazy(() => import('@pages/Vendors/VendorListing'));
const VendorDetail = lazy(() => import('@pages/Vendors/VendorDetail'));
const VendorCategory = lazy(() => import('@pages/Vendors/VendorCategory'));

// Venue pages
const VenueListing = lazy(() => import('@pages/Venues/VenueListing'));
const VenueDetail = lazy(() => import('@pages/Venues/VenueDetail'));

// Dashboard pages
const Dashboard = lazy(() => import('@pages/Dashboard'));
const MyWedding = lazy(() => import('@pages/Dashboard/MyWedding'));
const GuestList = lazy(() => import('@pages/Dashboard/GuestList'));
const WeddingWebsite = lazy(() => import('@pages/Dashboard/WeddingWebsite'));
const Budget = lazy(() => import('@pages/Dashboard/Budget'));
const Checklist = lazy(() => import('@pages/Dashboard/Checklist'));
const MyBookings = lazy(() => import('@pages/Dashboard/MyBookings'));
const Messages = lazy(() => import('@pages/Dashboard/Messages'));
const Settings = lazy(() => import('@pages/Dashboard/Settings'));

// Vendor Dashboard
const VendorDashboard = lazy(() => import('@pages/Vendors/Portal/Dashboard'));
const VendorProfile = lazy(() => import('@pages/Vendors/Portal/Profile'));
const VendorBookings = lazy(() => import('@pages/Vendors/Portal/Bookings'));
const VendorCalendar = lazy(() => import('@pages/Vendors/Portal/Calendar'));

// Admin pages
const AdminDashboard = lazy(() => import('@pages/Admin/Dashboard'));
const LiveDocs = lazy(() => import('@pages/Admin/LiveDocs'));
const AdminUsers = lazy(() => import('@pages/Admin/Users'));
const AdminVendors = lazy(() => import('@pages/Admin/Vendors'));
const AdminVenues = lazy(() => import('@pages/Admin/Venues'));
const AdminSettings = lazy(() => import('@pages/Admin/Settings'));
const AdminUserDetail = lazy(() => import('@pages/Admin/UserDetail'));
const AdminBookings = lazy(() => import('@pages/Admin/Bookings'));
const AdminRevenue = lazy(() => import('@pages/Admin/Revenue'));
const AdminCategories = lazy(() => import('@pages/Admin/Categories'));
const AdminReviews = lazy(() => import('@pages/Admin/Reviews'));
const AdminAuditLog = lazy(() => import('@pages/Admin/AuditLog'));

// Venue Owner pages
const VenueOwnerDashboard = lazy(() => import('@pages/VenueOwner/Dashboard'));
const VenueOwnerProfile = lazy(() => import('@pages/VenueOwner/Profile'));
const VenueOwnerPhotos = lazy(() => import('@pages/VenueOwner/Photos'));
const VenueOwnerBookings = lazy(() => import('@pages/VenueOwner/Bookings'));
const VenueOwnerAmenities = lazy(() => import('@pages/VenueOwner/Amenities'));
const VenueOwnerAvailability = lazy(() => import('@pages/VenueOwner/Availability'));
const VenueOwnerReviews = lazy(() => import('@pages/VenueOwner/Reviews'));

// Coordinator pages
const CoordinatorDashboard = lazy(() => import('@pages/Coordinator/Dashboard'));

// Guest pages
const GuestTravelStay = lazy(() => import('@pages/Guest/TravelStay'));
const GuestPhotoAlbum = lazy(() => import('@pages/Guest/PhotoAlbum'));
const GuestGiftRegistry = lazy(() => import('@pages/Guest/GiftRegistry'));
const CoordinatorWeddingView = lazy(() => import('@pages/Coordinator/WeddingView'));
const CoordinatorCalendar = lazy(() => import('@pages/Coordinator/Calendar'));
const CoordinatorVendorNetwork = lazy(() => import('@pages/Coordinator/VendorNetwork'));

// Guest Portal pages
const GuestHome = lazy(() => import('@pages/Guest/Home'));
const GuestRsvp = lazy(() => import('@pages/Guest/Rsvp'));
const GuestSchedule = lazy(() => import('@pages/Guest/Schedule'));

// Planning Tools
const PlanningTools = lazy(() => import('@pages/PlanningTools'));

// Static pages
const PrivacyPolicy = lazy(() => import('@pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@pages/TermsOfService'));

// Public RSVP page (no auth, no layout - standalone page)
const RsvpPage = lazy(() => import('@pages/RsvpPage'));

// Error pages
const NotFound = lazy(() => import('@pages/NotFound'));

// Loading fallback
const PageLoader = () => (
  <div className="page-loader">
    <Loader size="lg" />
  </div>
);

function App() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes with main layout */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          
          {/* Vendor routes */}
          <Route path="/vendors" element={<VendorListing />} />
          <Route path="/vendors/:category" element={<VendorCategory />} />
          <Route path="/vendors/:category/:slug" element={<VendorDetail />} />
          
          {/* Venue routes */}
          <Route path="/venues" element={<VenueListing />} />
          <Route path="/venues/:slug" element={<VenueDetail />} />
          
          {/* Planning Tools */}
          <Route path="/planning-tools" element={<PlanningTools />} />
          
          {/* Static pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
        </Route>

        {/* Auth routes — under MainLayout for header/footer */}
        <Route element={<MainLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Protected dashboard routes (couples) */}
        <Route
          element={
            <PrivateRoute roles={['user', 'couple', 'admin', 'super_admin']}>
              <DashboardLayout />
            </PrivateRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/my-wedding" element={<MyWedding />} />
          <Route path="/dashboard/guests" element={<GuestList />} />
          <Route path="/dashboard/website" element={<WeddingWebsite />} />
          <Route path="/dashboard/budget" element={<Budget />} />
          <Route path="/dashboard/checklist" element={<Checklist />} />
          <Route path="/dashboard/bookings" element={<MyBookings />} />
          <Route path="/dashboard/messages" element={<Messages />} />
          <Route path="/dashboard/settings" element={<Settings />} />
        </Route>

        {/* Protected vendor routes */}
        <Route
          element={
            <PrivateRoute roles={['vendor', 'admin', 'super_admin']}>
              <DashboardLayout type="vendor" />
            </PrivateRoute>
          }
        >
          <Route path="/vendor" element={<VendorDashboard />} />
          <Route path="/vendor/profile" element={<VendorProfile />} />
          <Route path="/vendor/bookings" element={<VendorBookings />} />
          <Route path="/vendor/calendar" element={<VendorCalendar />} />
          <Route path="/vendor/messages" element={<Messages />} />
          <Route path="/vendor/settings" element={<Settings />} />
        </Route>

        {/* Protected admin routes (super_admin only) */}
        <Route
          element={
            <PrivateRoute roles={['admin', 'super_admin']}>
              <DashboardLayout type="admin" />
            </PrivateRoute>
          }
        >
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/docs" element={<LiveDocs />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/vendors" element={<AdminVendors />} />
          <Route path="/admin/venues" element={<AdminVenues />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/users/:id" element={<AdminUserDetail />} />
          <Route path="/admin/bookings" element={<AdminBookings />} />
          <Route path="/admin/revenue" element={<AdminRevenue />} />
          <Route path="/admin/categories" element={<AdminCategories />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/audit" element={<AdminAuditLog />} />
        </Route>

        {/* Protected venue owner routes */}
        <Route
          element={
            <PrivateRoute roles={['venue_owner', 'super_admin', 'admin']}>
              <DashboardLayout type="venue_owner" />
            </PrivateRoute>
          }
        >
          <Route path="/venue" element={<VenueOwnerDashboard />} />
          <Route path="/venue/profile" element={<VenueOwnerProfile />} />
          <Route path="/venue/photos" element={<VenueOwnerPhotos />} />
          <Route path="/venue/bookings" element={<VenueOwnerBookings />} />
          <Route path="/venue/amenities" element={<VenueOwnerAmenities />} />
          <Route path="/venue/availability" element={<VenueOwnerAvailability />} />
          <Route path="/venue/reviews" element={<VenueOwnerReviews />} />
        </Route>

        {/* Protected coordinator routes */}
        <Route
          element={
            <PrivateRoute roles={['coordinator', 'super_admin', 'admin']}>
              <DashboardLayout type="coordinator" />
            </PrivateRoute>
          }
        >
          <Route path="/coordinator" element={<CoordinatorDashboard />} />
          <Route path="/coordinator/wedding/:id" element={<CoordinatorWeddingView />} />
          <Route path="/coordinator/calendar" element={<CoordinatorCalendar />} />
          <Route path="/coordinator/vendors" element={<CoordinatorVendorNetwork />} />
        </Route>

        {/* Protected guest portal routes */}
        <Route
          element={
            <PrivateRoute roles={['guest', 'super_admin', 'admin']}>
              <GuestLayout />
            </PrivateRoute>
          }
        >
          <Route path="/guest" element={<GuestHome />} />
          <Route path="/guest/rsvp" element={<GuestRsvp />} />
          <Route path="/guest/schedule" element={<GuestSchedule />} />
          <Route path="/guest/travel" element={<GuestTravelStay />} />
          <Route path="/guest/photos" element={<GuestPhotoAlbum />} />
          <Route path="/guest/registry" element={<GuestGiftRegistry />} />
        </Route>

        {/* Public RSVP page (standalone, no layout wrapper) */}
        <Route path="/rsvp/:subdomain" element={<RsvpPage />} />

        {/* Catch-all routes */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

export default App;
